<template>
  <Button
    variant="link"
    :class="['mb-4', customClass]"
    :pt="buttonPt"
    v-tooltip="tooltipText"
    :aria-label="tooltipText"
    @click="handleClick"
  >
    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">arrow_back</span>
    {{ labelText }}
  </Button>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

/**
 * BackButton Component
 *
 * A reusable back navigation button: a thin wrapper around PrimeVue Button
 * (`variant="link"`) with a Material Icons arrow_back icon followed by the label.
 * The tooltip text is shown via `v-tooltip` and also used as the `aria-label`.
 * Can navigate back in history or to a specific route.
 * Attributes that are not props (e.g. `id`, `data-test`) fall through to the
 * rendered `<button>`.
 *
 * @component
 * @example
 * <!-- Simple usage (navigates back) -->
 * <BackButton />
 * 
 * @example
 * <!-- Navigate to specific route -->
 * <BackButton to="/app/bots" />
 * 
 * @example
 * <!-- Custom label and tooltip -->
 * <BackButton label="Return to Dashboard" tooltip="Go back to dashboard" />
 * 
 * @example
 * <!-- Additional classes -->
 * <BackButton class="mt-8" />
 */

const { t } = useI18n();
const router = useRouter();

/**
 * Component props
 */
const props = defineProps({
  /**
   * Target route path. If provided, navigates to this route instead of going back.
   * @type {string}
   */
  to: {
    type: String,
    default: null
  },
  
  /**
   * Custom label text. If not provided, uses i18n common.backButton.
   * @type {string}
   */
  label: {
    type: String,
    default: null
  },
  
  /**
   * Custom tooltip text. If not provided, uses i18n common.backButtonTooltip.
   * @type {string}
   */
  tooltip: {
    type: String,
    default: null
  },
  
  /**
   * Additional CSS classes to merge with default styling.
   * @type {string}
   */
  class: {
    type: String,
    default: ''
  }
});

/**
 * Computed label text (uses prop or i18n default)
 */
const labelText = computed(() => {
  return props.label || t('common.backButton');
});

/**
 * Computed tooltip text (uses prop or i18n default)
 */
const tooltipText = computed(() => {
  return props.tooltip || t('common.backButtonTooltip');
});

/**
 * Custom CSS classes
 */
const customClass = computed(() => props.class);

/**
 * Pass-through for the PrimeVue Button root.
 * The DIY button had no padding, so its icon lined up with the page content.
 * PrimeVue Button adds form-field padding, which would push it 1rem to the right.
 * A Tailwind `p-0` class cannot win here: PrimeVue styles are unlayered
 * (`cssLayer: false`), so the padding is zeroed with an inline style instead.
 */
const buttonPt = { root: { style: { padding: 0 } } };

/**
 * Handle click event
 * Navigates to specific route if `to` prop is provided, otherwise goes back in history.
 */
const handleClick = () => {
  if (props.to) {
    router.push(props.to);
  } else {
    router.back();
  }
};
</script>
